/**
 * What one test actually did — shown when its result row is expanded.
 *
 * The point is handover. A QA engineer should be able to open a failure, read the
 * request that broke, copy it, and give a developer something reproducible instead
 * of "the lesson editor failed on QA". Everything here was recorded by the run
 * itself: the pages visited, every network call with its timing and status, the
 * bodies of our own API calls, and any console error.
 *
 * Secrets are masked at capture time, not here — passwords and tokens never reach
 * storage in the first place.
 */

import { useEffect, useState } from 'react';

interface NetworkCall {
  method: string;
  url: string;
  status?: number;
  ms?: number;
  requestBody?: string;
  responseBody?: string;
  failure?: string;
}

export interface Diagnostics {
  title: string;
  file: string;
  status: string;
  visited: string[];
  calls: NetworkCall[];
  consoleErrors: string[];
  error?: string;
  environment: string;
}

function statusColour(status?: number, failure?: string): string {
  if (failure) return 'hsl(var(--fail))';
  if (status === undefined) return 'hsl(var(--text-faint))';
  if (status >= 500) return 'hsl(var(--fail))';
  if (status >= 400) return 'hsl(var(--warn))';
  return 'hsl(var(--pass))';
}

/** A plain-text summary, so a failure can be pasted into a ticket or a chat. */
function reproductionText(d: Diagnostics): string {
  const interesting = d.calls.filter((c) => (c.status ?? 0) >= 400 || c.failure);
  return [
    `Test: ${d.title}`,
    `File: ${d.file}`,
    `Environment: ${d.environment}`,
    `Result: ${d.status}`,
    '',
    'Steps (pages visited in order):',
    ...d.visited.map((v, i) => `  ${i + 1}. ${v}`),
    '',
    ...(d.error ? ['Failure:', `  ${d.error.replace(/\n/g, '\n  ')}`, ''] : []),
    ...(interesting.length
      ? ['Failing requests:', ...interesting.map((c) => `  ${c.method} ${c.url} → ${c.failure ?? c.status}`), '']
      : []),
    ...(d.consoleErrors.length ? ['Console errors:', ...d.consoleErrors.map((e) => `  ${e}`)] : []),
  ].join('\n');
}

/** Pulls "Expected: x / Received: y" out of a Playwright failure message. */
function expectation(error?: string): { expected?: string; actual?: string } {
  if (!error) return {};
  const expected = /Expected(?: pattern| string| value)?:\s*(.+)/.exec(error)?.[1]?.trim();
  const actual = /(?:Received(?: string| value)?|Actual):\s*(.+)/.exec(error)?.[1]?.trim();
  return { expected, actual };
}

export function TestDiagnostics({
  runId,
  testTitle,
  test,
  runFinished,
}: {
  runId: string;
  testTitle: string;
  /** The row this was expanded from — known immediately, unlike the diagnostics. */
  test?: { title: string; suite: string; file: string; app: string; status: string; unstable?: boolean; attempts?: number };
  runFinished?: boolean;
}) {
  const [data, setData] = useState<Diagnostics | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none'>('loading');
  const [copied, setCopied] = useState(false);
  const [openCall, setOpenCall] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/runs/${runId}/diagnostics?test=${encodeURIComponent(testTitle)}`)
      .then((r) => r.json())
      .then((r: { diagnostics: Diagnostics[] }) => {
        if (cancelled) return;
        const found = r.diagnostics?.[0];
        setData(found ?? null);
        setState(found ? 'ready' : 'none');
      })
      .catch(() => !cancelled && setState('none'));
    return () => { cancelled = true; };
  }, [runId, testTitle]);

  if (state === 'loading') {
    return <p className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>Loading…</p>;
  }

  // What the test IS — available immediately, from the row itself. Shown whether or
  // not diagnostics exist, because "which test is this and where does it live" is
  // the first question and it should never require waiting for a container to exit.
  const identity = test && (
    <dl className="mb-3 grid gap-x-4 gap-y-1 text-xs" style={{ gridTemplateColumns: 'auto 1fr' }}>
      <dt style={{ color: 'hsl(var(--text-faint))' }}>What it checks</dt>
      <dd>{[test.suite, test.title].filter(Boolean).join(' › ')}</dd>
      <dt style={{ color: 'hsl(var(--text-faint))' }}>Repository</dt>
      <dd>{test.app || 'unknown'}</dd>
      <dt style={{ color: 'hsl(var(--text-faint))' }}>Spec file</dt>
      <dd className="break-all">{test.file}</dd>
      <dt style={{ color: 'hsl(var(--text-faint))' }}>Outcome</dt>
      <dd>
        {test.unstable
          ? `passed, but only on attempt ${test.attempts} — treat as failing`
          : test.status}
      </dd>
    </dl>
  );

  if (state === 'none' || !data) {
    return (
      <div className="px-4 py-3 text-xs">
        {identity}
        <p style={{ color: 'hsl(var(--text-faint))' }}>
          {runFinished
            ? 'No network log for this test. Diagnostics are collected when the run finishes and are removed when the run drops out of history.'
            : 'The network log and steps arrive when the run finishes — they are collected from the runner container as it exits.'}
        </p>
      </div>
    );
  }

  const failing = data.calls.filter((c) => (c.status ?? 0) >= 400 || c.failure);

  return (
    <div className="px-4 py-3 text-xs" data-testid="test-diagnostics">
      {identity}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          className="btn btn-secondary px-2.5 py-1 text-xs"
          onClick={() => {
            void navigator.clipboard?.writeText(reproductionText(data));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          data-testid="copy-reproduction"
        >
          {copied ? 'Copied' : 'Copy reproduction'}
        </button>
        <span style={{ color: 'hsl(var(--text-faint))' }}>
          {data.calls.length} request{data.calls.length === 1 ? '' : 's'}
          {failing.length > 0 && <span className="text-fail"> · {failing.length} failing</span>}
          {data.consoleErrors.length > 0 && <span className="text-warn"> · {data.consoleErrors.length} console error{data.consoleErrors.length === 1 ? '' : 's'}</span>}
        </span>
      </div>

      {data.error && (
        <div className="mb-3 rounded-md p-2 bg-fail-soft">
          {/* Expected vs actual pulled to the front — it is the sentence that says
              what went wrong, and it is otherwise buried in a stack. */}
          {(() => {
            const { expected, actual } = expectation(data.error);
            return expected || actual ? (
              <dl className="mb-2 grid gap-x-4 gap-y-1" style={{ gridTemplateColumns: 'auto 1fr' }}>
                {expected && <><dt style={{ color: 'hsl(var(--text-faint))' }}>Expected</dt><dd className="text-pass">{expected}</dd></>}
                {actual && <><dt style={{ color: 'hsl(var(--text-faint))' }}>Actual</dt><dd className="text-fail">{actual}</dd></>}
              </dl>
            ) : null;
          })()}
          <pre className="whitespace-pre-wrap text-fail">{data.error}</pre>
        </div>
      )}

      {data.visited.length > 0 && (
        <div className="mb-3">
          <div className="label mb-1">Steps — pages visited in order</div>
          <ol className="space-y-0.5">
            {data.visited.map((url, i) => (
              <li key={i} className="truncate" style={{ color: 'hsl(var(--text-muted))' }} title={url}>
                {i + 1}. {url}
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.calls.length > 0 && (
        <div className="mb-3">
          <div className="label mb-1">Network</div>
          <div className="max-h-64 overflow-auto rounded-md" style={{ border: '1px solid hsl(var(--border))' }}>
            {data.calls.map((call, i) => (
              <div key={i} style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}>
                <button
                  className="flex w-full items-baseline gap-2 px-2 py-1 text-left hover:opacity-80"
                  onClick={() => setOpenCall(openCall === i ? null : i)}
                  disabled={!call.requestBody && !call.responseBody}
                >
                  <span className="w-12 shrink-0 tabular" style={{ color: 'hsl(var(--text-faint))' }}>{call.method}</span>
                  <span className="w-10 shrink-0 tabular" style={{ color: statusColour(call.status, call.failure) }}>
                    {call.failure ? 'ERR' : call.status}
                  </span>
                  <span className="flex-1 truncate" title={call.url}>{call.url.replace(/^https?:\/\//, '')}</span>
                  <span className="shrink-0 tabular" style={{ color: 'hsl(var(--text-faint))' }}>
                    {call.ms !== undefined ? `${call.ms}ms` : ''}
                  </span>
                </button>

                {openCall === i && (
                  <div className="space-y-2 px-3 pb-2">
                    {call.failure && <p className="text-fail">{call.failure}</p>}
                    {call.requestBody && (
                      <div>
                        <div className="label mb-0.5">Request</div>
                        <pre className="log max-h-40 overflow-auto rounded p-2">{call.requestBody}</pre>
                      </div>
                    )}
                    {call.responseBody && (
                      <div>
                        <div className="label mb-0.5">Response</div>
                        <pre className="log max-h-40 overflow-auto rounded p-2">{call.responseBody}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.consoleErrors.length > 0 && (
        <div>
          <div className="label mb-1">Console errors</div>
          <ul className="space-y-0.5">
            {data.consoleErrors.map((e, i) => (
              <li key={i} className="text-warn">{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
